export type NauticalChartType =
  | "vertti"
  | "s57"
  | "raster"
  | "maastokartta"
  | "combined";

export interface LayerSettings {
  weather: boolean;
  harbor: boolean;
  crowdPoi: boolean;
  septic: boolean;
  ais: boolean;
  networkTiles: boolean;
  ice: boolean;
  navWarnings: boolean;
  forecast: boolean;
  heatMap: boolean;
  raster2025: boolean;
  /**
   * Premium-only special raster layer (erikoiskartat).
   * Toggled separately from the main raster chart selection.
   */
  rasterSpecial: boolean;
  avoinMerikartta: boolean;
  s57: boolean;
  /** Free Finnish Maastokartta vector basemap (full-Finland PMTiles). */
  maastokartta: boolean;
  /** Premium combined Maastokartta + S57 ENC package. */
  combined: boolean;
  scoutRoutes: boolean;
  scoutRouting: boolean;
  savedPlaces: boolean;
  recreationSites: boolean;
  restriction01SpeedLimit: boolean;
  restriction02NoWake: boolean;
  restriction03NoWindsurfing: boolean;
  restriction04NoJetSki: boolean;
  restriction05NoMotorPower: boolean;
  restriction06NoAnchoring: boolean;
}

export type NetworkTilesUiMetric = "download" | "upload" | "latency" | "rsrp";
export type NetworkTilesUiTech = "4g" | "5g";
export type NetworkTilesUiOperator = "05" | "12" | "91";

/**
 * Derives the active chart / basemap type from layer settings.
 * Ensures we always return a valid chart type, even if flags are inconsistent.
 */
export function getActiveNauticalChart(
  settings: LayerSettings,
  s57Available: boolean,
): NauticalChartType {
  if (settings.maastokartta) return "maastokartta";
  if (s57Available && settings.combined) return "combined";
  if (settings.raster2025) return "raster";
  if (s57Available && settings.s57) return "s57";
  // Fallback to Vertti if nothing else is explicitly enabled
  return "vertti";
}

/**
 * Normalizes layer settings so exactly one chart / basemap flag is true.
 * Priority: maastokartta > combined (premium) > raster2025 > s57 (premium) > Vertti.
 */
export function normalizeNauticalChartSettings(
  settings: LayerSettings,
  s57Available: boolean,
): LayerSettings {
  const active = getActiveNauticalChart(settings, s57Available);
  return {
    ...settings,
    maastokartta: active === "maastokartta",
    combined: s57Available && active === "combined",
    avoinMerikartta: active === "vertti",
    s57: s57Available && active === "s57",
    raster2025: active === "raster",
  };
}
